/**
 * A small non-modal popover: a real <button> that toggles a panel rendered
 * right after it in the DOM (so Tab moves from the button into the panel).
 * Opening moves focus to the panel so a screen reader reads it; Esc and a
 * click outside close it, and Esc returns focus to the button. Works on
 * touch and keyboard — unlike a `title` tooltip, which only shows on hover.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export function Popover({
  label,
  title,
  children,
  className = 'tag',
  buttonLabel,
}: {
  /** What the button shows. */
  label: ReactNode;
  /** The panel's heading (also its accessible name). */
  title: string;
  children: ReactNode;
  className?: string;
  /** Accessible name for the button when `label` is abbreviated. */
  buttonLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onDown = (e: PointerEvent | MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span className="popover-wrap" ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`popover-btn ${className}`}
        aria-expanded={open}
        aria-controls={open ? `${id}-panel` : undefined}
        aria-label={buttonLabel}
        onClick={() => setOpen(!open)}
      >
        {label}
        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" className="popover-caret">
          <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div
          ref={panel}
          id={`${id}-panel`}
          className="popover"
          role="dialog"
          aria-modal="false"
          aria-labelledby={`${id}-title`}
          tabIndex={-1}
          onBlur={(e) => {
            // Tabbing out of the panel (not into it or back to the button) closes it.
            const next = e.relatedTarget as Node | null;
            if (next && wrap.current && !wrap.current.contains(next)) setOpen(false);
          }}
        >
          <strong id={`${id}-title`} className="popover-title">
            {title}
          </strong>
          <div className="popover-body">{children}</div>
        </div>
      ) : null}
    </span>
  );
}
